import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Wallet } from "lucide-react";
import type {
  InvoicePaymentQueueItemDto,
  PaymentQueueListMeta,
  VendorSummaryDto,
} from "@procureflow/shared-types";
import { PageHeader, EmptyCard } from "@/components/page-header";
import { fetchWithAuth } from "@/lib/auth-client";
import { formatDate, formatAmount } from "@/lib/format";
import { cn } from "@/lib/utils";

// US-046 — Payment queue. Finance and Admin see Matched + Approved
// invoices waiting for payment. Mark-as-Paid lives on invoice detail
// (deferred until the detail page lands in a follow-up slice).
//
// URL persistence of filter/sort state is deferred — the existing list
// pages (PR / PO / GRN) keep filter state in-memory; matching that
// behaviour now and adding URL persistence in a follow-up keeps the
// payment-queue slice contained.

export const Route = createFileRoute("/invoices_/payment-queue")({
  head: () => ({
    meta: [
      { title: "Payment Queue — ProcureFlow" },
      {
        name: "description",
        content: "Matched and Approved invoices ready for payment.",
      },
    ],
  }),
  component: PaymentQueuePage,
});

type SortBy = "due_date" | "updated_at";
type SortDir = "asc" | "desc";
type Path = "all" | "auto_matched" | "finance_approved";

interface QueueState {
  rows: InvoicePaymentQueueItemDto[];
  meta: PaymentQueueListMeta | null;
  loading: boolean;
  error: string | null;
}

const PAGE_LIMIT = 20;

function buildQueryString(params: {
  vendor_id: string;
  invoice_date_from: string;
  invoice_date_to: string;
  po_number: string;
  path: Path;
  sort_by: SortBy;
  sort_dir: SortDir;
  page: number;
}): string {
  const q = new URLSearchParams({
    page: String(params.page),
    limit: String(PAGE_LIMIT),
    sort_by: params.sort_by,
    sort_dir: params.sort_dir,
  });
  if (params.vendor_id) q.set("vendor_id", params.vendor_id);
  if (params.invoice_date_from) q.set("invoice_date_from", params.invoice_date_from);
  if (params.invoice_date_to) q.set("invoice_date_to", params.invoice_date_to);
  if (params.po_number) q.set("po_number", params.po_number);
  if (params.path !== "all") q.set("path", params.path);
  return q.toString();
}

function pathDisplayLabel(status: "Matched" | "Approved"): string {
  return status === "Matched" ? "Auto-matched" : "Finance approved";
}

function isOverdueDate(dueIso: string, todayIso: string): boolean {
  // Compare YYYY-MM-DD strings lexicographically — equivalent to date
  // comparison and TZ-stable since both are UTC-anchored dates.
  return dueIso < todayIso;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
    d.getUTCDate(),
  ).padStart(2, "0")}`;
}

function readySinceLabel(updatedAtIso: string): string {
  const now = Date.now();
  const t = new Date(updatedAtIso).getTime();
  const hours = Math.floor((now - t) / (1000 * 60 * 60));
  if (hours < 24) return "Today";
  if (hours < 48) return "Yesterday";
  const days = Math.floor(hours / 24);
  if (days <= 30) return `${days} days`;
  const weeks = Math.floor(days / 7);
  return `${weeks} weeks`;
}

function PaymentQueuePage() {
  const [vendorId, setVendorId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [path, setPath] = useState<Path>("all");
  const [sortBy, setSortBy] = useState<SortBy>("due_date");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(1);

  const [state, setState] = useState<QueueState>({
    rows: [],
    meta: null,
    loading: true,
    error: null,
  });

  const fetchQueue = useCallback(async () => {
    if (import.meta.env.DEV) {
      const { mswReady } = await import("@/mocks/browser");
      await mswReady;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const qs = buildQueryString({
        vendor_id: vendorId,
        invoice_date_from: dateFrom,
        invoice_date_to: dateTo,
        po_number: poNumber,
        path,
        sort_by: sortBy,
        sort_dir: sortDir,
        page,
      });
      const res = await fetchWithAuth(`/api/invoices/payment-queue?${qs}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Failed to load payment queue.");
      }
      const { data, meta } = (await res.json()) as {
        data: InvoicePaymentQueueItemDto[];
        meta: PaymentQueueListMeta;
      };
      setState({ rows: data, meta, loading: false, error: null });
    } catch (err) {
      setState({
        rows: [],
        meta: null,
        loading: false,
        error: err instanceof Error ? err.message : "Unexpected error.",
      });
    }
  }, [vendorId, dateFrom, dateTo, poNumber, path, sortBy, sortDir, page]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Page reset on filter change — keeps invariant that pagination is
  // recomputed from page 1 whenever the result set shape changes.
  function resetAndSet<T>(setter: (v: T) => void, value: T): void {
    setter(value);
    setPage(1);
  }

  const toggleSort = useCallback(
    (col: SortBy) => {
      if (sortBy !== col) {
        setSortBy(col);
        setSortDir("asc");
      } else {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      }
      setPage(1);
    },
    [sortBy],
  );

  const today = todayIso();
  const totalPages = state.meta ? Math.max(1, Math.ceil(state.meta.total / PAGE_LIMIT)) : 1;
  const vendorOptions: VendorSummaryDto[] = state.meta?.vendor_options ?? [];
  const pathOptions = state.meta?.path_options ?? [];

  return (
    <div className="space-y-6" data-testid="payment-queue-section">
      <PageHeader
        title="Payment Queue"
        description="Matched and Finance-approved invoices ready for payment."
      />

      <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
        {/* Filter strip */}
        <div className="border-b border-border bg-muted/20 px-4 py-3 flex flex-wrap items-center gap-2">
          <select
            aria-label="Vendor"
            value={vendorId}
            onChange={(e) => resetAndSet(setVendorId, e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-primary/30"
          >
            <option value="">Vendor: All</option>
            {vendorOptions.map((v) => (
              <option key={v.id} value={v.id}>
                {v.company_name}
              </option>
            ))}
          </select>

          <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-background p-0.5 text-xs">
            <PathPill
              active={path === "all"}
              onClick={() => resetAndSet(setPath, "all")}
              label="All"
            />
            {pathOptions.includes("auto_matched") && (
              <PathPill
                active={path === "auto_matched"}
                onClick={() => resetAndSet(setPath, "auto_matched")}
                label="Auto-matched"
              />
            )}
            {pathOptions.includes("finance_approved") && (
              <PathPill
                active={path === "finance_approved"}
                onClick={() => resetAndSet(setPath, "finance_approved")}
                label="Finance approved"
              />
            )}
          </div>

          <input
            type="date"
            aria-label="Invoice date from"
            value={dateFrom}
            onChange={(e) => resetAndSet(setDateFrom, e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
          />
          <input
            type="date"
            aria-label="Invoice date to"
            value={dateTo}
            onChange={(e) => resetAndSet(setDateTo, e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
          />
          <input
            type="search"
            aria-label="PO number"
            value={poNumber}
            onChange={(e) => resetAndSet(setPoNumber, e.target.value)}
            placeholder="PO number"
            className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
          />

          <div className="ml-auto text-xs text-muted-foreground">
            {state.meta ? `${state.meta.total} ${state.meta.total === 1 ? "invoice" : "invoices"}` : "—"}
          </div>
        </div>

        {/* Body */}
        {state.loading ? (
          <div className="px-5 py-12 text-center text-xs text-muted-foreground">Loading…</div>
        ) : state.error ? (
          <div className="px-5 py-12 text-center text-xs text-destructive">{state.error}</div>
        ) : state.rows.length === 0 ? (
          <div className="px-5 py-10">
            <EmptyCard
              icon={Wallet}
              title="No invoices awaiting payment."
              description="Matched and Approved invoices ready for payment will appear here."
            />
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs text-muted-foreground">
              <tr>
                <th className="px-5 py-2.5 text-left font-medium">Invoice #</th>
                <th className="px-3 py-2.5 text-left font-medium">Vendor</th>
                <th className="px-3 py-2.5 text-left font-medium">PO #</th>
                <th className="px-3 py-2.5 text-left font-medium">Invoice Date</th>
                <SortableTh
                  label="Due Date"
                  active={sortBy === "due_date"}
                  dir={sortDir}
                  onClick={() => toggleSort("due_date")}
                />
                <th className="px-3 py-2.5 text-right font-medium">Amount</th>
                <th className="px-3 py-2.5 text-left font-medium">Path</th>
                <SortableTh
                  label="Ready Since"
                  active={sortBy === "updated_at"}
                  dir={sortDir}
                  onClick={() => toggleSort("updated_at")}
                />
              </tr>
            </thead>
            <tbody>
              {state.rows.map((inv) => {
                const overdue = isOverdueDate(inv.due_date, today);
                return (
                  <tr
                    key={inv.id}
                    className="border-t border-border hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-5 py-3 font-mono text-xs">
                      <Link
                        to="/invoices/$id"
                        params={{ id: inv.id }}
                        className="hover:text-primary transition-colors"
                      >
                        {inv.invoice_number}
                      </Link>
                    </td>
                    <td className="px-3 py-3">{inv.vendor.company_name}</td>
                    <td className="px-3 py-3 font-mono text-xs text-muted-foreground">
                      {inv.purchase_order.po_number}
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">
                      {formatDate(inv.invoice_date)}
                    </td>
                    <td
                      className={cn(
                        "px-3 py-3",
                        overdue ? "text-destructive font-medium" : "text-muted-foreground",
                      )}
                    >
                      <span className="inline-flex items-center gap-2">
                        {formatDate(inv.due_date)}
                        {overdue && (
                          <span className="inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-destructive">
                            Overdue
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium">
                      {formatAmount(inv.total_amount)}
                    </td>
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium">
                        {pathDisplayLabel(inv.status)}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">
                      {readySinceLabel(inv.updated_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {state.meta && state.meta.total > PAGE_LIMIT && (
          <div className="flex items-center justify-between border-t border-border px-5 py-3 text-xs text-muted-foreground">
            <div>
              Page {page} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded-md border border-border px-2 py-1 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded-md border border-border px-2 py-1 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PathPill({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-2.5 py-1 rounded-md transition",
        active
          ? "bg-card text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
    </button>
  );
}

function SortableTh({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) {
  return (
    <th className="px-3 py-2.5 text-left font-medium">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "inline-flex items-center gap-1 text-xs uppercase tracking-wide",
          active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
        )}
      >
        {label}
        <span aria-hidden className="text-[9px]">
          {active ? (dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );
}
